import { onBeforeUnmount, ref } from "vue";
import type { VectorizeFullResult } from "~/lib/api/types/imageProcessor";
import type { VectorizeTaskStreamEvent } from "~/lib/api/types/vectorizeTasks";
import {
    followVectorizeTask,
    type VectorizeTaskAuth,
    VectorizeTaskRequestError,
} from "~/lib/api/vectorizeTasks";
import { svgDataUrl } from "~/lib/vectorize/playback";
import { useVectorizePlayback } from "./useVectorizePlayback";

function currentAuth(): VectorizeTaskAuth | undefined {
    const { token } = useTokenStore();
    const { slug } = useSlugStore();

    return token && slug ? { slug, token } : undefined;
}

/** Waits before following a task again after its stream dropped. */
const RECONNECT_DELAYS_MS = [500, 1000, 2000, 5000];

export type VectorizeTaskOutcome =
    | { message: string; status: "failed" }
    | { result: VectorizeFullResult; status: "done" }
    | { status: "cancelled" };

/**
 * Shows a queued task in the player: follows its event stream from the start
 * of the current attempt, so joining a run halfway shows everything so far
 * and then goes on live. A finished task replays.
 *
 * Watching is all this does — stopping it, closing the dialog or leaving the
 * page never affects the task. A stream that drops (an image-processor
 * instance going down) is followed again; its replay starts with the
 * attempt, which resets the player.
 */
export function useVectorizeTaskPlayback() {
    const { t } = useI18n();
    const playback = useVectorizePlayback();
    const store = useVectorizeTaskStore();
    const taskId = ref<string>();
    const outcome = ref<VectorizeTaskOutcome>();

    let controller: AbortController | undefined;
    let sourceUrl: string | undefined;

    function stop() {
        controller?.abort();
        controller = undefined;
        taskId.value = undefined;
        outcome.value = undefined;

        if (sourceUrl) URL.revokeObjectURL(sourceUrl);
        sourceUrl = undefined;
    }

    async function follow(id: string): Promise<void> {
        if (taskId.value === id && controller) return;

        stop();
        taskId.value = id;

        const own = new AbortController();

        controller = own;

        const task = store.taskById(id);
        const isEdit = task?.kind === "svg-edit";
        const source = await loadSource(id, isEdit);

        if (own.signal.aborted) return;

        let started = false;

        function begin() {
            const current = store.taskById(id);

            playback.reset(source);
            playback.alignClock(current?.startedAt, current?.finishedAt);
            started = false;
        }

        function apply(event: VectorizeTaskStreamEvent) {
            switch (event.type) {
                case "cancelled":
                    playback.fail(t("vectorize.queue.cancelled_hint"));
                    outcome.value = { status: "cancelled" };
                    break;

                case "error":
                    playback.push(event);
                    playback.fail(event.message);
                    outcome.value = {
                        message: event.message,
                        status: "failed",
                    };
                    break;

                case "result":
                    playback.push(event);
                    playback.finish(event.result);
                    outcome.value = { result: event.result, status: "done" };
                    break;

                case "start":
                    if (!started) {
                        if (isEdit) {
                            playback.startEdit();
                        } else {
                            playback.startWithoutUpscale();
                        }
                    }

                    started = true;
                    playback.push(event);
                    break;

                case "task-attempt":
                    begin();
                    break;

                case "upscale":
                    if (event.status === "running") {
                        playback.startUpscale();
                        started = true;
                    } else {
                        playback.finishUpscale(
                            event.image
                                ? `data:${event.image.mediaType};base64,${event.image.base64}`
                                : undefined,
                        );
                        playback.startVectorize();
                    }

                    break;

                default:
                    playback.push(event);
            }
        }

        begin();

        // stop() aborts from outside, which TypeScript's narrowing cannot see.
        function isStopped(): boolean {
            return own.signal.aborted;
        }

        for (let attempt = 0; !isStopped(); attempt++) {
            const auth = currentAuth();

            if (!auth) return;

            try {
                const end = await followVectorizeTask(
                    auth,
                    id,
                    own.signal,
                    apply,
                );

                if (end === "finished") return;
            } catch (error) {
                if (isStopped()) return;

                // The task is gone (dropped, or expired): nothing to follow.
                if (
                    error instanceof VectorizeTaskRequestError &&
                    error.status === 404
                ) {
                    return;
                }
            }

            const delay =
                RECONNECT_DELAYS_MS[
                    Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
                ];

            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }

    /** The first frame: the task's own input, as it was queued. */
    async function loadSource(id: string, isEdit: boolean): Promise<string> {
        try {
            const blob = await store.loadSource(id);

            if (isEdit) return svgDataUrl(await blob.text());

            sourceUrl = URL.createObjectURL(blob);

            return sourceUrl;
        } catch {
            return "";
        }
    }

    onBeforeUnmount(stop);

    return { follow, outcome, playback, stop, taskId };
}
